import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ArrowLeft,
  CalendarDays,
  Loader2,
  LockKeyhole,
  MapPin,
  PackagePlus,
  RefreshCw,
  Search,
  Send,
  Phone,
  SlidersHorizontal,
  Truck,
  X,
} from "lucide-react";

import i18next from "i18next";

import {
  reisorProDriverRequest,
  reisorProMarketRequest,
} from "../utils/reisorProMarketHttp.js";

import DriverContactActions from "./DriverContactActions.jsx";
import VehicleTypePicker from "./vehicle/VehicleTypePicker.jsx";
import { sanitizeExternalExcerpt } from "./vehicle/externalExcerpt.js";
import { vehicleTypeLabel } from "./vehicle/vehicleCatalog.js";
import { marketDisplayPlace } from "../utils/driverMarketCyrillic.mjs";
import { activeFreightsRequestForRole } from "../utils/vehicleFreightRoleFlow.js";
import ReisorTransportVisual from "./vehicle/ReisorTransportVisual.jsx";
import { isNativeReisorTransport, nativeTransportPrimaryLabel } from "./vehicle/reisorTransportVisualCore.js";

const COPY = {
  ru: {
    title: "Доступный транспорт",
    subtitle: "REISOR и внешние источники в одном списке",
    from: "Откуда",
    to: "Куда",
    fromPlaceholder: "Город или регион",
    toPlaceholder: "Город или регион",
    body: "Кузов",
    allBodies: "Любой кузов",
    partial: "Догруз",
    find: "Найти",
    reset: "Сбросить",
    refresh: "Обновить",
    filters: "Фильтры",
    saveSearch: "Сохранить поиск",
    savingSearch: "Сохраняем…",
    savedSearch: "Поиск сохранён",
    saveSearchError: "Не удалось сохранить поиск.",
    minCapacity: "Минимум тонн",
    minVolume: "Минимум м³",
    readyBy: "Свободна не позже",
    sourceTelegram: "Telegram",
    details: "Подробнее",
    detailsTitle: "О машине",
    back: "Назад",
    loading: "Загрузка машин…",
    empty: "По этим условиям свободных машин пока нет.",
    error: "Не удалось загрузить машины.",
    found: "Найдено машин",
    available: "Свободна с",
    expires: "Актуально до",
    capacity: "Грузоподъёмность",
    volume: "Объём",
    capacityMissing: "Тоннаж: —",
    volumeMissing: "Объём: —",
    negotiable: "Цена договорная",
    contactLocked: "Контакт закрыт",
    contactHint: "Контакты REISOR защищены. Отправьте свой груз через платформу.",
    sendFreight: "Отправить заказ",
    chooseFreight: "Выберите груз",
    chooseFreightHint: "Сначала выберите, какой именно активный груз отправить этой машине.",
    activeFreights: "Ваши активные грузы",
    loadingFreights: "Загрузка ваших грузов…",
    sendThisFreight: "Отправить этот груз",
    loadingDateLabel: "Загрузка",
    noFreight: "У вас нет активного груза.",
    createAndSend: "Создать груз и отправить",
    createNewFreight: "Создать новый груз",
    sending: "Отправляем…",
    sent: "Груз отправлен владельцу машины.",
    noPush: "Заказ сохранён в REISOR, но активный push-канал сейчас недоступен.",
    alreadySent: "Заказ уже отправлен этой машине.",
    inviteError: "Не удалось отправить груз этой машине.",
    inviteSystemError: "REISOR не смог сохранить отправку. Повторите ещё раз.",
    inviteFreightInactive: "Этот груз больше не активен. Обновите список грузов.",
    inviteVehicleUnavailable: "Эта машина уже недоступна. Обновите список машин.",
    inviteRoleSession: "Сессия текущей роли устарела. Войдите в эту роль заново.",
    inviteOwnerUnavailable: "Владелец машины сейчас недоступен для получения заказа.",
    cooldown: "Этот груз уже недавно отправлялся этой машине.",
    close: "Закрыть",
    sourceReisor: "REISOR",
    sourceAll: "Все", sourceExternal: "Внешние", call: "Позвонить", callback: "Запросить звонок", callbackSent: "Запрос на звонок отправлен владельцу машины.", callbackNoPush: "У владельца машины сейчас нет активного push-канала. Отправьте заказ через REISOR.", callbackError: "Не удалось отправить запрос на звонок.", ownVehicle: "Это ваша машина.", workCountries: "Работает по странам",
    confirmedFree: "Свободен", unconfirmed: "Статус не подтверждён", direction: "Направление", updated: "Обновлено",
    vehicle: "Машина",
    tons: "т",
    cubic: "м³",
  },
  uz: {
    title: "Машина қидириш",
    subtitle: "REISOR'даги бўш машиналар",
    from: "Қаердан",
    to: "Қаерга",
    fromPlaceholder: "Шаҳар ёки ҳудуд",
    toPlaceholder: "Шаҳар ёки ҳудуд",
    body: "Кузов",
    allBodies: "Барча кузовлар",
    partial: "Қўшимча юк",
    find: "Топиш",
    reset: "Тозалаш",
    refresh: "Янгилаш",
    filters: "Фильтрлар",
    saveSearch: "Қидирувни сақлаш",
    savingSearch: "Сақланмоқда…",
    savedSearch: "Қидирув сақланди",
    saveSearchError: "Қидирувни сақлаб бўлмади.",
    minCapacity: "Минимал тонна",
    minVolume: "Минимал м³",
    readyBy: "Кечикмасдан бўш",
    sourceTelegram: "Telegram",
    details: "Батафсил",
    detailsTitle: "Машина ҳақида",
    back: "Орқага",
    loading: "Машиналар юкланмоқда…",
    empty: "Бу шартлар бўйича ҳозирча бўш машина йўқ.",
    error: "Машиналарни юклаб бўлмади.",
    found: "Топилган машиналар",
    available: "Бўш вақти",
    expires: "Амал қилади",
    capacity: "Юк кўтариши",
    volume: "Ҳажми",
    capacityMissing: "Тоннаж: —",
    volumeMissing: "Ҳажм: —",
    negotiable: "Нарх келишилади",
    contactLocked: "Контакт ёпиқ",
    contactHint: "REISOR контактлари ҳимояланган. Юкингизни платформа орқали юборинг.",
    sendFreight: "Буюртма юбориш",
    chooseFreight: "Юкни танланг",
    chooseFreightHint: "Аввало шу машинага қайси фаол юкни юборишни танланг.",
    activeFreights: "Сизнинг фаол юкларингиз",
    loadingFreights: "Юкларингиз юкланмоқда…",
    sendThisFreight: "Шу юкни юбориш",
    loadingDateLabel: "Юклаш",
    noFreight: "Сизда фаол юк йўқ.",
    createAndSend: "Юк яратиб юбориш",
    createNewFreight: "Янги юк яратиш",
    sending: "Юборилмоқда…",
    sent: "Юк машина эгасига юборилди.",
    noPush: "Юк сақланди, аммо машинанинг актив push-канали ҳозир йўқ.",
    inviteError: "Юкни бу машинага юбориб бўлмади.",
    inviteFreightInactive: "Бу юк энди фаол эмас. Юклар рўйхатини янгиланг.",
    inviteVehicleUnavailable: "Бу машина энди мавжуд эмас. Машиналар рўйхатини янгиланг.",
    inviteRoleSession: "Жорий роль сессияси эскирган. Шу рольга қайта киринг.",
    inviteOwnerUnavailable: "Машина эгаси ҳозир буюртмани қабул қила олмайди.",
    cooldown: "Бу юк яқинда шу машинага юборилган.",
    close: "Ёпиш",
    sourceReisor: "REISOR",
    sourceAll: "Барчаси", sourceExternal: "Ташқи", call: "Қўнғироқ", callback: "Қўнғироқ сўраш", callbackSent: "Қўнғироқ сўрови машина эгасига юборилди.", callbackNoPush: "Машина эгасида ҳозир фаол push-канал йўқ. Буюртмани REISOR орқали юборинг.", callbackError: "Қўнғироқ сўровини юбориб бўлмади.", ownVehicle: "Бу сизнинг машинангиз.", workCountries: "Ишлайдиган давлатлар",
    confirmedFree: "Бўш", unconfirmed: "Ҳолати тасдиқланмаган", direction: "Йўналиш", updated: "Янгиланган",
    vehicle: "Машина",
    tons: "т",
    cubic: "м³",
  },
  tr: {
    title: "Araç ara",
    subtitle: "REISOR'daki müsait araçlar",
    from: "Nereden",
    to: "Nereye",
    fromPlaceholder: "Şehir veya bölge",
    toPlaceholder: "Şehir veya bölge",
    body: "Kasa",
    allBodies: "Tüm kasa tipleri",
    partial: "Ek yük",
    find: "Ara",
    reset: "Sıfırla",
    refresh: "Yenile",
    filters: "Filtreler",
    saveSearch: "Aramayı kaydet",
    savingSearch: "Kaydediliyor…",
    savedSearch: "Arama kaydedildi",
    saveSearchError: "Arama kaydedilemedi.",
    minCapacity: "Minimum ton",
    minVolume: "Minimum m³",
    readyBy: "En geç müsait",
    sourceTelegram: "Telegram",
    details: "Detay",
    detailsTitle: "Araç hakkında",
    back: "Geri",
    loading: "Araçlar yükleniyor…",
    empty: "Bu koşullara uygun müsait araç yok.",
    error: "Araçlar yüklenemedi.",
    found: "Bulunan araçlar",
    available: "Müsait",
    expires: "Geçerli",
    capacity: "Kapasite",
    volume: "Hacim",
    capacityMissing: "Tonaj: —",
    volumeMissing: "Hacim: —",
    negotiable: "Fiyat görüşülür",
    contactLocked: "İletişim kilitli",
    contactHint: "REISOR iletişim bilgileri korunur. Yükünüzü platform üzerinden gönderin.",
    sendFreight: "Sipariş gönder",
    chooseFreight: "Yük seç",
    chooseFreightHint: "Önce bu araca hangi aktif yükü göndereceğinizi seçin.",
    activeFreights: "Aktif yükleriniz",
    loadingFreights: "Yükleriniz yükleniyor…",
    sendThisFreight: "Bu yükü gönder",
    loadingDateLabel: "Yükleme",
    noFreight: "Aktif yükünüz yok.",
    createAndSend: "Yük oluştur ve gönder",
    createNewFreight: "Yeni yük oluştur",
    sending: "Gönderiliyor…",
    sent: "Yük araç sahibine gönderildi.",
    noPush: "Yük kaydedildi ancak aracın aktif push kanalı şu anda yok.",
    inviteError: "Yük bu araca gönderilemedi.",
    inviteFreightInactive: "Bu yük artık aktif değil. Yük listesini yenileyin.",
    inviteVehicleUnavailable: "Bu araç artık müsait değil. Araç listesini yenileyin.",
    inviteRoleSession: "Geçerli rol oturumu eskidi. Bu role yeniden giriş yapın.",
    inviteOwnerUnavailable: "Araç sahibi şu anda siparişi alamıyor.",
    cooldown: "Bu yük kısa süre önce bu araca gönderildi.",
    close: "Kapat",
    sourceReisor: "REISOR",
    sourceAll: "Tümü", sourceExternal: "Dış kaynak", call: "Ara", callback: "Arama iste", callbackSent: "Arama isteği araç sahibine gönderildi.", callbackNoPush: "Araç sahibinin şu anda aktif push kanalı yok. Siparişi REISOR üzerinden gönderin.", callbackError: "Arama isteği gönderilemedi.", ownVehicle: "Bu sizin aracınız.", workCountries: "Çalıştığı ülkeler",
    confirmedFree: "Müsait", unconfirmed: "Durum doğrulanmadı", direction: "Güzergâh", updated: "Güncellendi",
    vehicle: "Araç",
    tons: "t",
    cubic: "m³",
  },
};

function text(value) {
  return String(value == null ? "" : value).trim();
}

function vehicleDisplayPlace(raw) {
  const normalized = marketDisplayPlace(raw, "ru");
  if (!normalized) return "";

  return normalized
    .toLocaleLowerCase("ru-RU")
    .replace(/(^|[\s\-/,(])([\p{L}])/gu, (match, prefix, letter) =>
      `${prefix}${letter.toLocaleUpperCase("ru-RU")}`
    );
}


function languageCode() {
  const raw = text(i18next.language).toLowerCase();
  if (raw.startsWith("uz")) return "uz";
  if (raw.startsWith("tr")) return "tr";
  return "ru";
}

function cleanToken(value) {
  return text(value).replace(/^Bearer\s+/i, "");
}

function formatDate(value, lang) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const locale =
    lang === "uz"
      ? "uz-UZ"
      : lang === "tr"
        ? "tr-TR"
        : "ru-RU";

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatAmount(value, currency, lang) {
  const amount = Number(value);

  if (!Number.isFinite(amount) || amount <= 0) {
    return "";
  }

  const locale =
    lang === "uz"
      ? "uz-UZ"
      : lang === "tr"
        ? "tr-TR"
        : "ru-RU";

  return `${new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(amount)} ${text(currency)}`;
}

function vehicleName(item) {
  const explicit = text(item?.vehicle?.car);
  if (explicit) return explicit;
  return [
    text(item?.vehicle?.carBrand),
    text(item?.vehicle?.carModel),
    text(item?.vehicle?.carYear),
  ].filter(Boolean).join(" ");
}

function vehicleBodyLabel(item, lang) {
  const code = text(item?.bodyType).toUpperCase();
  const other = text(item?.bodyTypeOther);
  if (code === "OTHER" && other) return other;
  return vehicleTypeLabel(code, lang) || text(item?.bodyType) || other || "—";
}

function positiveMetric(value, unit) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? `${number} ${unit}` : "";
}

function vehicleInviteErrorMessage(code, t) {
  const normalized = text(code).toUpperCase();
  if (normalized === "INVITE_COOLDOWN") return t.cooldown;
  if (["MARKET_POST_NOT_OWNED_OR_ACTIVE", "ACTIVE_OWN_MARKET_POST_REQUIRED"].includes(normalized)) {
    return t.inviteFreightInactive || t.inviteError;
  }
  if (["AVAILABLE_VEHICLE_NOT_FOUND", "DRIVER_NOT_FOUND", "DRIVER_NOT_ELIGIBLE"].includes(normalized)) {
    return t.inviteVehicleUnavailable || t.inviteError;
  }
  if (["MARKET_ROLE_REQUIRED", "MARKET_AUTH_REQUIRED", "DRIVER_AUTH_REQUIRED"].includes(normalized)) {
    return t.inviteRoleSession || t.inviteError;
  }
  if (["VEHICLE_OWNER_UNAVAILABLE", "NO_ACTIVE_PUSH_CHANNEL"].includes(normalized)) {
    return t.inviteOwnerUnavailable || t.inviteError;
  }
  if (["DRIVER_INVITE_PERSIST_FAILED", "VEHICLE_MARKET_FAILED"].includes(normalized)) {
    return t.inviteSystemError || t.inviteError;
  }
  return t.inviteError;
}

export default function ReisorVehicleMarket({
  token = "",
  className = "",
  showHeader = true,
  initialFilters = null,
  initialFiltersKey = "",
  onSaveSearch = null,
  onCreateFreightForVehicle = null,
  onCreateFreightForDriver = null,
  actorRole = "",
  resumeVehicle = null,
  resumeVehicleKey = "",
}) {
  const lang = languageCode();
  const t = COPY[lang] || COPY.ru;
  const normalizedActorRole = text(actorRole).toUpperCase();

  const [filters, setFilters] = useState({
    from: "",
    to: "",
    bodyType: "",
    capacityTons: "",
    volumeM3: "",
    readyAt: "",
    partialLoad: false,
  });

  const [items, setItems] = useState([]);
  const [marketTotal, setMarketTotal] = useState(0);
  const [sourceCounts, setSourceCounts] = useState({});
  const [sourceSegment, setSourceSegment] = useState("ALL");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  const [posts, setPosts] = useState([]);
  const [postsBusy, setPostsBusy] = useState(false);
  const [inviteBusyId, setInviteBusyId] = useState("");
  const [sendNotice, setSendNotice] = useState({ kind: "", message: "" });
  const [callNotice, setCallNotice] = useState({ kind: "", message: "" });

  const [saveSearchState, setSaveSearchState] = useState({
    busy: false,
    ok: false,
    message: "",
  });

  const seqRef = useRef(0);

  const normalizedToken =
    useMemo(
      () => cleanToken(token),
      [token]
    );

  const load = useCallback(
    async (nextFilters = filters, { silent = false, append = false, offset = 0 } = {}) => {
      if (!normalizedToken) {
        setItems([]);
        setMarketTotal(0);
        setSourceCounts({});
        setError(t.error);
        return;
      }

      const seq = ++seqRef.current;

      if (!silent) {
        setBusy(true);
      }

      setError("");

      try {
        const params = new URLSearchParams();
        params.set("limit", "100");
        if (offset > 0) params.set("offset", String(offset));

        const from = text(nextFilters?.from);
        const to = text(nextFilters?.to);
        const bodyType = text(nextFilters?.bodyType).toUpperCase();

        if (from) params.set("from", from);
        if (to) params.set("to", to);
        if (bodyType) params.set("bodyType", bodyType);

        const capacityTons =
          Number(
            nextFilters?.capacityTons
          );

        if (
          Number.isFinite(capacityTons) &&
          capacityTons > 0
        ) {
          params.set(
            "capacityTons",
            String(capacityTons)
          );
        }

        const volumeM3 =
          Number(
            nextFilters?.volumeM3
          );

        if (
          Number.isFinite(volumeM3) &&
          volumeM3 > 0
        ) {
          params.set(
            "volumeM3",
            String(volumeM3)
          );
        }

        const readyAt =
          text(nextFilters?.readyAt)
            .slice(0, 10);

        if (readyAt) {
          params.set(
            "readyAt",
            readyAt
          );
        }

        if (nextFilters?.partialLoad) {
          params.set(
            "partialLoad",
            "true"
          );
        }

        params.set("source", sourceSegment);
        const { response, data } = await reisorProDriverRequest(
          `/api/vehicle-market/items?${params.toString()}`,
          { token: normalizedToken }
        );
        if (seq !== seqRef.current) return;
        if (!response?.ok || !data?.ok) throw new Error(text(data?.error) || "VEHICLE_MARKET_FAILED");
        const rows = Array.isArray(data?.items) ? data.items : [];
        setItems((prev) => {
          if (!append) return rows;
          const seen = new Set(prev.map((item) => text(item?.id)));
          return [...prev, ...rows.filter((item) => !seen.has(text(item?.id)))];
        });
        setMarketTotal(Number(data?.total || 0));
        setSourceCounts(data?.sources && typeof data.sources === "object" ? data.sources : {});

      } catch {
        if (seq !== seqRef.current) return;
        setItems([]);
        setMarketTotal(0);
        setSourceCounts({});
        setError(t.error);
      } finally {
        if (seq === seqRef.current) {
          setBusy(false);
        }
      }
    },
    [filters, normalizedToken, normalizedActorRole, sourceSegment, t.error]
  );

  useEffect(() => {
    void load(
      {
        from: "",
        to: "",
        bodyType: "",
        capacityTons: "",
        volumeM3: "",
        readyAt: "",
        partialLoad: false,
      },
      { silent: false }
    );
  }, [normalizedToken]);

  const sourceSwitchReadyRef = useRef(false);
  useEffect(() => {
    if (!sourceSwitchReadyRef.current) {
      sourceSwitchReadyRef.current = true;
      return;
    }
    void load(filters, { silent: false });
    // sourceSegment is the explicit server-side market partition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceSegment]);

  useEffect(() => {
    if (!initialFiltersKey) {
      return;
    }

    const next = {
      from: text(initialFilters?.from),
      to: text(initialFilters?.to),
      bodyType:
        text(initialFilters?.bodyType),

      capacityTons:
        initialFilters?.capacityTons == null
          ? ""
          : String(
              initialFilters.capacityTons
            ),

      volumeM3:
        initialFilters?.volumeM3 == null
          ? ""
          : String(
              initialFilters.volumeM3
            ),

      readyAt:
        text(initialFilters?.readyAt)
          .slice(0, 10),

      partialLoad:
        Boolean(
          initialFilters?.partialLoad
        ),
    };

    setSelectedItem(null);
    setFilters(next);
    setFiltersOpen(false);

    setSaveSearchState({
      busy: false,
      ok: false,
      message: "",
    });

    void load(
      next,
      { silent: false }
    );

    // initialFiltersKey is the explicit replay trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialFiltersKey,
    normalizedToken,
  ]);

  useEffect(() => {
    if (!resumeVehicleKey || !resumeVehicle) return;
    setSelectedItem(resumeVehicle);
    setSendTarget(null);
    setSendNotice({ kind: "", message: "" });
  }, [resumeVehicleKey, resumeVehicle]);

  function resetFilters() {
    const empty = {
      from: "",
      to: "",
      bodyType: "",
      capacityTons: "",
      volumeM3: "",
      readyAt: "",
      partialLoad: false,
    };

    setFilters(empty);
    setFiltersOpen(false);
    void load(empty);
  }

  const hasFilters = Boolean(
    text(filters.from) ||
    text(filters.to) ||
    text(filters.bodyType) ||
    Number(filters.capacityTons) > 0 ||
    Number(filters.volumeM3) > 0 ||
    text(filters.readyAt) ||
    filters.partialLoad
  );

  async function saveCurrentVehicleSearch() {
    if (
      typeof onSaveSearch !== "function" ||
      !hasFilters ||
      saveSearchState.busy
    ) {
      return;
    }

    setSaveSearchState({
      busy: true,
      ok: false,
      message: "",
    });

    try {
      const result =
        await onSaveSearch({
          from: text(filters.from),
          to: text(filters.to),

          // Pass current REISOR bodyType unchanged.
          bodyType:
            text(filters.bodyType),

          capacityTons:
            Number(filters.capacityTons) > 0
              ? Number(
                  filters.capacityTons
                )
              : null,

          volumeM3:
            Number(filters.volumeM3) > 0
              ? Number(
                  filters.volumeM3
                )
              : null,

          readyAt:
            text(filters.readyAt) ||
            null,

          partialLoad:
            Boolean(
              filters.partialLoad
            ),
        });

      if (
        !result?.ok ||
        !result?.item
      ) {
        throw new Error(
          text(result?.error) ||
          "SAVE_FAILED"
        );
      }

      setSaveSearchState({
        busy: false,
        ok: true,
        message: t.savedSearch,
      });
    } catch {
      setSaveSearchState({
        busy: false,
        ok: false,
        message: t.saveSearchError,
      });
    }
  }

  function canSendFreightToVehicle(item) {
    const source = text(item?.source).toUpperCase();
    const ownerRole = text(item?.ownerRole).toUpperCase();
    const targetKind = text(item?.targetKind).toUpperCase();
    if (targetKind === "DRIVER_DIRECTORY") return Boolean(text(item?.targetDriverId));
    return source === "REISOR_USER" && ["DRIVER", "EXPEDITOR"].includes(ownerRole);
  }

  const loadOwnPosts = useCallback(async () => {
    if (!normalizedToken) return;
    setPostsBusy(true);
    setPosts([]);
    try {
      const requestMeta = activeFreightsRequestForRole(normalizedActorRole);
      if (!requestMeta) throw new Error("FREIGHT_SENDER_ROLE_UNSUPPORTED");

      const request = requestMeta.transport === "driver"
        ? reisorProDriverRequest(requestMeta.path, { token: normalizedToken })
        : reisorProMarketRequest(requestMeta.path, { token: normalizedToken });

      const { response, data } = await request;
      if (!response?.ok || !data?.ok) throw new Error(data?.error || "POSTS_FAILED");

      const rows = Array.isArray(data?.items) ? data.items.slice() : [];
      rows.sort((a, b) => {
        const aTime = new Date(a?.updatedAt || a?.createdAt || 0).getTime() || 0;
        const bTime = new Date(b?.updatedAt || b?.createdAt || 0).getTime() || 0;
        return bTime - aTime;
      });
      setPosts(rows);
    } catch {
      setPosts([]);
    } finally {
      setPostsBusy(false);
    }
  }, [normalizedActorRole, normalizedToken]);

  async function openSendFreight(item) {
    if (!canSendFreightToVehicle(item)) return;
    setSendNotice({ kind: "", message: "" });
    setSendTarget(item);
    await loadOwnPosts();
  }

  async function sendPostToVehicle(item, post) {
    const vehicleId = text(item?.id);
    const ownerRole = text(item?.ownerRole).toUpperCase();
    const targetKind = text(item?.targetKind).toUpperCase();
    const targetDriverId = text(item?.targetDriverId);
    const postId = text(post?.id);
    if (!postId || inviteBusyId) return;
    if (targetKind === "DRIVER_DIRECTORY" && !targetDriverId) return;
    if (targetKind !== "DRIVER_DIRECTORY" && (!vehicleId || !ownerRole)) return;

    setInviteBusyId(postId);
    setSendNotice({ kind: "", message: "" });

    try {
      const { response, data } = targetKind === "DRIVER_DIRECTORY"
        ? await reisorProDriverRequest(
            `/api/vehicle-market/drivers/${encodeURIComponent(targetDriverId)}/invite`,
            {
              token: normalizedToken,
              method: "POST",
              body: { postId },
            }
          )
        : await reisorProDriverRequest(
            `/api/vehicle-market/items/${encodeURIComponent(vehicleId)}/invite`,
            {
              token: normalizedToken,
              method: "POST",
              body: {
                postId,
                vehicleOwnerRole: ownerRole,
              },
            }
          );

      if (!response?.ok || !data?.ok) {
        const error = new Error(data?.error || "VEHICLE_INVITE_FAILED");
        error.code = data?.error || "VEHICLE_INVITE_FAILED";
        throw error;
      }

      const sent = Number(data?.sent ?? data?.delivery?.sent ?? 0);
      const accepted = data?.accepted === true || Boolean(data?.invite?.id);
      const replay = data?.replay === true || text(data?.deliveryStatus).toUpperCase() === "RECENT_REPLAY";

      setSendNotice({
        kind: sent > 0 || replay ? "success" : accepted ? "warn" : "error",
        message: replay ? t.alreadySent : sent > 0 ? t.sent : accepted ? t.noPush : t.inviteError,
      });

      if (sent > 0 || replay) {
        window.setTimeout(() => setSendTarget(null), 900);
      }
    } catch (error) {
      const code = text(error?.code || error?.message);
      console.warn("[REISOR vehicle invite]", code || "VEHICLE_INVITE_FAILED");
      setSendNotice({
        kind: "error",
        message: vehicleInviteErrorMessage(code, t),
      });
    } finally {
      setInviteBusyId("");
    }
  }

  const sendSheet = sendTarget ? (
    <div className="fixed inset-0 z-[10080] flex items-end bg-slate-950/60 p-0 sm:items-center sm:justify-center sm:p-4" role="dialog" aria-modal="true" data-reisor-vehicle-send-sheet-layer="p3">
      <div
        className="w-full overflow-y-auto rounded-t-[28px] bg-white p-4 shadow-2xl dark:bg-slate-900 sm:max-w-lg sm:rounded-[28px]"
        style={{
          maxHeight: "calc(100dvh - max(12px, env(safe-area-inset-top, 0px)))",
          paddingBottom: "max(20px, calc(env(safe-area-inset-bottom, 0px) + 16px))",
        }}
        data-reisor-vehicle-send-sheet-safe-area="p3"
      >
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-xl font-black text-slate-950 dark:text-white">{t.chooseFreight}</h3>
            <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">{t.chooseFreightHint}</p>
          </div>
          <button type="button" onClick={() => setSendTarget(null)} className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200" aria-label={t.close}>
            <X size={20} />
          </button>
        </div>
  
        {sendNotice.message ? (
          <div className={`mt-3 rounded-2xl p-3 text-sm font-black ${
            sendNotice.kind === "success"
              ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200"
              : sendNotice.kind === "warn"
                ? "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-200"
                : "bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-200"
          }`}>
            {sendNotice.message}
          </div>
        ) : null}
  
        {postsBusy ? (
          <div className="py-8 text-center text-sm font-bold text-slate-500">
            <Loader2 size={21} className="mx-auto mb-2 animate-spin" />{t.loadingFreights}
          </div>
        ) : null}
  
        {!postsBusy && posts.length ? (
          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="text-sm font-black text-slate-950 dark:text-white">{t.activeFreights}</div>
              <div className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-black text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">{posts.length}</div>
            </div>
            <div className="grid gap-2" data-reisor-active-freight-chooser="p3">
              {posts.map((post, index) => {
                const truckTypes = Array.isArray(post?.truckTypes)
                  ? post.truckTypes.map((value) => vehicleTypeLabel(text(value).toUpperCase(), lang) || text(value)).filter(Boolean)
                  : [];
                return (
                  <button
                    key={post.id}
                    type="button"
                    disabled={Boolean(inviteBusyId)}
                    onClick={() => void sendPostToVehicle(sendTarget, post)}
                    className="rounded-2xl border border-slate-200 p-3 text-left shadow-sm transition active:scale-[0.995] disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950/40"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-sm font-black text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                        {inviteBusyId === post.id ? <Loader2 size={18} className="animate-spin" /> : index + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[15px] font-black leading-5 text-slate-950 dark:text-white">
                          {[text(post?.from), text(post?.to)].filter(Boolean).join(" → ") || t.vehicle}
                        </div>
                        <div className="mt-1 text-sm font-bold text-slate-700 dark:text-slate-200">
                          {text(post?.cargo) || "—"}{post?.weightTons ? ` · ${post.weightTons} ${t.tons}` : ""}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                          {truckTypes.length ? <span>{truckTypes.join(", ")}</span> : null}
                          {post?.loadingDate ? <span>{t.loadingDateLabel}: {formatDate(post.loadingDate, lang)}</span> : null}
                        </div>
                        <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-black text-blue-700 dark:text-blue-300">
                          <Send size={14} />{t.sendThisFreight}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
  
        {!postsBusy && !posts.length ? (
          <div className="mt-4 rounded-2xl bg-slate-50 p-4 text-sm font-bold text-slate-500 dark:bg-slate-950 dark:text-slate-400">{t.noFreight}</div>
        ) : null}
  
        {!postsBusy ? (
          <button
            type="button"
            onClick={() => {
              const vehicle = sendTarget;
              setSendTarget(null);
              if (text(vehicle?.targetKind).toUpperCase() === "DRIVER_DIRECTORY") {
                onCreateFreightForDriver?.({
                  id: text(vehicle?.targetDriverId),
                  fullName: text(vehicle?.fullName),
                });
              } else {
                onCreateFreightForVehicle?.(vehicle);
              }
            }}
            className="mt-4 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-black text-white dark:bg-white dark:text-slate-950"
          >
            <PackagePlus size={19} />{posts.length ? t.createNewFreight : t.createAndSend}
          </button>
        ) : null}
      </div>
    </div>
  ) : null;

  async function requestVehicleCall(item) {
    const publicPhone = text(item?.contact?.phone);
    const access = text(item?.contactAccess).toUpperCase();
    if (publicPhone && ["PUBLIC_SOURCE", "PUBLIC_REISOR"].includes(access)) {
      window.location.href = `tel:${publicPhone.replace(/[^+\d]/g, "")}`;
      return;
    }

    if (item?.isOwn === true) {
      setCallNotice({ kind: "warn", message: t.ownVehicle || "Это ваша машина." });
      return;
    }

    try {
      const directoryTarget = text(item?.targetKind).toUpperCase() === "DRIVER_DIRECTORY";
      const requestPath = directoryTarget
        ? `/api/vehicle-market/drivers/${encodeURIComponent(text(item?.targetDriverId))}/request-call`
        : `/api/vehicle-market/items/${encodeURIComponent(text(item?.id))}/request-call`;
      const { response, data } = await reisorProDriverRequest(
        requestPath,
        { token: normalizedToken, method: "POST", body: directoryTarget ? {} : { vehicleOwnerRole: text(item?.ownerRole).toUpperCase() } }
      );
      if (!response?.ok || !data?.ok) throw new Error();

      if (Number(data?.sent || 0) > 0 || text(data?.deliveryStatus).toUpperCase() === "SENT") {
        setCallNotice({ kind: "success", message: t.callbackSent || "Запрос на звонок отправлен владельцу машины." });
        return;
      }

      if (text(data?.deliveryStatus).toUpperCase() === "NO_ACTIVE_PUSH_CHANNEL") {
        setCallNotice({ kind: "warn", message: t.callbackNoPush || "У владельца машины сейчас нет активного push-канала. Отправьте заказ через REISOR." });
        return;
      }

      setCallNotice({ kind: "warn", message: t.callbackNoPush || "Запрос принят, но активный push-канал владельца не подтверждён." });
    } catch {
      setCallNotice({ kind: "error", message: t.callbackError || "Не удалось отправить запрос на звонок." });
    }
  }

  if (selectedItem) {
    const fixedPrice =
      text(selectedItem?.price?.mode).toUpperCase() === "FIXED"
        ? formatAmount(
            selectedItem?.price?.amount,
            selectedItem?.price?.currency,
            lang
          )
        : "";
    const car = vehicleName(selectedItem);
    const selectedNative = isNativeReisorTransport(selectedItem);
    const selectedRoute = [vehicleDisplayPlace(selectedItem?.from), vehicleDisplayPlace(selectedItem?.to)].filter(Boolean).join(" → ");
    const selectedCountries = Array.isArray(selectedItem?.workCountryLabels) && selectedItem.workCountryLabels.length
      ? selectedItem.workCountryLabels.map(text).filter(Boolean)
      : Array.isArray(selectedItem?.workCountries) ? selectedItem.workCountries.map(text).filter(Boolean) : [];
    const contactPhone = text(
      selectedItem?.contact?.phone ||
      selectedItem?.contactPhone ||
      selectedItem?.phone
    );

    return (
      <section className={`space-y-4 ${className}`} data-reisor-vehicle-market="v1">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setSelectedItem(null)}
            className="flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-700 bg-slate-900 text-slate-200"
            aria-label={t.back}
          >
            <ArrowLeft size={21} />
          </button>
          <div>
            <h2 className="text-xl font-black text-white">{t.detailsTitle}</h2>
          </div>
        </div>

        <div className="border-b border-slate-800 pb-4">
          {selectedNative ? <ReisorTransportVisual item={selectedItem} className="mb-4 h-[190px] w-full" /> : null}
          {selectedNative ? (
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="min-w-0 text-[21px] font-black leading-tight text-white">{nativeTransportPrimaryLabel(selectedItem, t.vehicle)}</div>
              <div className={`shrink-0 rounded-full px-3 py-1 text-xs font-black ${selectedItem?.availabilityConfirmed === true ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
                {selectedItem?.availabilityConfirmed === true ? t.confirmedFree : t.unconfirmed}
              </div>
            </div>
          ) : null}
          {selectedRoute ? (
            <div className="flex items-start gap-2 text-[21px] font-black leading-tight text-white">
              <MapPin size={19} className="mt-0.5 shrink-0 text-blue-400" />
              <span className="min-w-0 break-words">{selectedRoute}</span>
            </div>
          ) : selectedCountries.length ? (
            <div className="flex flex-wrap gap-2">
              <span className="mr-1 text-sm font-black text-slate-300">{t.direction}:</span>
              {selectedCountries.slice(0, 12).map((country) => <span key={country} className="rounded-full bg-slate-800 px-2.5 py-1 text-xs font-bold text-slate-200">{country}</span>)}
            </div>
          ) : null}

          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px] font-semibold text-slate-300">
            <span className="font-black text-blue-300">{vehicleBodyLabel(selectedItem, lang)}</span>
            <span className="text-slate-600">·</span><span>{positiveMetric(selectedItem?.capacityTons, t.tons) || t.capacityMissing}</span>
            <span className="text-slate-600">·</span><span>{positiveMetric(selectedItem?.volumeM3, t.cubic) || t.volumeMissing}</span>
            {selectedItem?.partialLoad ? <><span className="text-slate-600">·</span><span>{t.partial}</span></> : null}
          </div>

          {car ? (
            <div className="mt-3 flex items-center gap-2 text-[14px] text-slate-400"><Truck size={15} /><span>{car}</span></div>
          ) : null}

          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-slate-500">
            {selectedNative ? (
              <span>{t.updated}: {formatDate(selectedItem?.freshnessAt || selectedItem?.updatedAt, lang)}</span>
            ) : (
              <><span>{t.available}: {formatDate(selectedItem?.availableFrom, lang)}</span><span>{t.expires}: {formatDate(selectedItem?.expiresAt, lang)}</span></>
            )}
          </div>

          {sanitizeExternalExcerpt(selectedItem?.rawText || selectedItem?.comment) ? (
            <div className="mt-4 whitespace-pre-wrap text-[15px] leading-6 text-slate-300">
              {sanitizeExternalExcerpt(selectedItem?.rawText || selectedItem?.comment)}
            </div>
          ) : null}
        </div>

        <div className="pt-1">
          {callNotice.message ? (
            <div className={`mb-3 rounded-2xl p-3 text-sm font-black ${
              callNotice.kind === "success"
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200"
                : callNotice.kind === "warn"
                  ? "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-200"
                  : "bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-200"
            }`} data-reisor-call-notice="p14">
              {callNotice.message}
            </div>
          ) : null}
          <div className="mb-3 text-[14px] font-bold text-emerald-300">{fixedPrice || t.negotiable}</div>
          {canSendFreightToVehicle(selectedItem) ? (
            <button
              type="button"
              onClick={() => void openSendFreight(selectedItem)}
              className="mb-3 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 text-sm font-black text-white shadow-sm active:scale-[0.99]"
            >
              <Send size={19} />{t.sendFreight}
            </button>
          ) : null}
          {contactPhone ? (
            <div className="space-y-2" data-reisor-public-contact="p15">
              <a
                href={`tel:${contactPhone.replace(/[^+\d]/g, "")}`}
                className="block rounded-2xl border border-slate-700 bg-slate-900 px-4 py-3 text-center text-base font-black text-white no-underline"
                data-reisor-public-phone="p15"
              >
                {contactPhone}
              </a>
              <DriverContactActions phone={contactPhone} variant="hub" />
            </div>
          ) : isNativeReisorTransport(selectedItem) && selectedItem?.isOwn !== true ? (
            <button type="button" onClick={() => void requestVehicleCall(selectedItem)} className="flex min-h-[50px] w-full items-center justify-center gap-2 rounded-2xl border border-slate-600 bg-slate-800 text-sm font-black text-white" data-reisor-callback-action="p14"><Phone size={18}/>{t.callback || "Запросить звонок"}</button>
          ) : isNativeReisorTransport(selectedItem) && selectedItem?.isOwn === true ? (
            <div className="rounded-2xl border border-slate-700 bg-slate-900 px-4 py-3 text-center text-sm font-black text-slate-300" data-reisor-own-vehicle="p14">{t.ownVehicle || "Это ваша машина."}</div>
          ) : (
            <div className="flex items-center gap-2 border-t border-slate-800 pt-3"><LockKeyhole size={16} className="text-slate-500" /><div><div className="text-[14px] font-black text-slate-300">{t.contactLocked}</div><div className="mt-0.5 text-[13px] text-slate-500">{t.contactHint}</div></div></div>
          )}
        </div>

        {sendSheet}

      </section>
    );
  }

  return (
    <section className={`space-y-4 ${className}`} data-reisor-vehicle-market="v1">
      <div className={`flex items-start gap-3 ${showHeader ? "justify-between" : "justify-end"}`} data-reisor-vehicle-market-toolbar="v1">
        {showHeader ? (
          <div>
            <h2 className="text-xl font-black text-white">
              {t.title}
            </h2>
            <p className="mt-1 text-sm text-slate-400">
              {t.subtitle}
            </p>
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFiltersOpen((value) => !value)}
            className={`flex h-10 items-center justify-center gap-2 rounded-xl border px-3 text-sm font-black ${
              filtersOpen || hasFilters
                ? "border-blue-500 bg-blue-600 text-white"
                : "border-slate-700 bg-slate-900 text-slate-200"
            }`}
            aria-expanded={filtersOpen}
            aria-label={t.filters}
            title={t.filters}
          >
            <SlidersHorizontal size={17} />
            <span>{t.filters}</span>
          </button>

          <button
            type="button"
            onClick={() => void load(filters)}
            disabled={busy}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-700 bg-slate-900 text-slate-200 disabled:opacity-50"
            aria-label={t.refresh}
            title={t.refresh}
          >
            <RefreshCw
              size={18}
              className={busy ? "animate-spin" : ""}
            />
          </button>
        </div>
      </div>

      {filtersOpen ? (
      <div className="rounded-2xl border border-slate-700/80 bg-slate-900/80 p-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[14px] font-bold text-slate-400">
              {t.from}
            </span>
            <input
              value={filters.from}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  from: event.target.value,
                }))
              }
              placeholder={t.fromPlaceholder}
              className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white outline-none focus:border-blue-500"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-[14px] font-bold text-slate-400">
              {t.to}
            </span>
            <input
              value={filters.to}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  to: event.target.value,
                }))
              }
              placeholder={t.toPlaceholder}
              className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white outline-none focus:border-blue-500"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-[14px] font-bold text-slate-400">
              {t.body}
            </span>
            <VehicleTypePicker
              value={filters.bodyType}
              onChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  bodyType: value,
                }))
              }
              domain="market"
              allowAll
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-[14px] font-bold text-slate-400">
              {t.minCapacity}
            </span>

            <input
              type="number"
              inputMode="decimal"
              min="0.1"
              step="0.1"
              value={
                filters.capacityTons
              }
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  capacityTons:
                    event.target.value,
                }))
              }
              placeholder="20"
              className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white outline-none focus:border-blue-500"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-[14px] font-bold text-slate-400">
              {t.minVolume}
            </span>

            <input
              type="number"
              inputMode="decimal"
              min="0.1"
              step="0.1"
              value={
                filters.volumeM3
              }
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  volumeM3:
                    event.target.value,
                }))
              }
              placeholder="86"
              className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white outline-none focus:border-blue-500"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-[14px] font-bold text-slate-400">
              {t.readyBy}
            </span>

            <input
              type="date"
              value={
                filters.readyAt
              }
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  readyAt:
                    event.target.value,
                }))
              }
              className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white outline-none focus:border-blue-500"
            />
          </label>

          <label className="flex min-h-[48px] items-center gap-3 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 sm:mt-5">
            <input
              type="checkbox"
              checked={filters.partialLoad}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  partialLoad: event.target.checked,
                }))
              }
              className="h-4 w-4"
            />
            <span className="text-sm font-bold text-slate-200">
              {t.partial}
            </span>
          </label>
        </div>

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={() => {
              setFiltersOpen(false);
              void load(filters);
            }}
            disabled={busy}
            className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-black text-white disabled:opacity-50"
          >
            {busy ? (
              <Loader2 size={17} className="animate-spin" />
            ) : (
              <Search size={17} />
            )}
            {t.find}
          </button>

          {typeof onSaveSearch === "function" && hasFilters ? (
            <button
              type="button"
              onClick={() => void saveCurrentVehicleSearch()}
              disabled={saveSearchState.busy}
              className={`flex min-h-[44px] items-center justify-center gap-2 rounded-xl border px-4 text-sm font-black disabled:opacity-50 ${
                saveSearchState.ok
                  ? "border-emerald-500 bg-emerald-600 text-white"
                  : "border-amber-500/70 bg-amber-500/10 text-amber-300"
              }`}
            >
              {saveSearchState.busy ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <span aria-hidden="true">☆</span>
              )}

              {saveSearchState.busy
                ? t.savingSearch
                : saveSearchState.ok
                  ? t.savedSearch
                  : t.saveSearch}
            </button>
          ) : null}

          <button
            type="button"
            onClick={resetFilters}
            disabled={busy}
            className="flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-950 px-4 text-sm font-bold text-slate-300 disabled:opacity-50"
          >
            <X size={16} />
            {t.reset}
          </button>
        </div>
      </div>
      ) : null}

      <div className="flex flex-wrap gap-2" data-reisor-market-source-tabs="p10">
        {[
          ["ALL", t.sourceAll || "Все", Number(sourceCounts.ALL || 0)],
          ["REISOR", t.sourceReisor || "REISOR", Number(sourceCounts.REISOR_USER || 0)],
          ["EXTERNAL", t.sourceExternal || "Внешние", Number(sourceCounts.EXTERNAL || sourceCounts.TELEGRAM || 0)],
        ].map(([key,label,count]) => (
          <button key={key} type="button" onClick={() => setSourceSegment(key)} className={`rounded-xl border px-3 py-2 text-xs font-black ${sourceSegment === key ? "border-blue-500 bg-blue-600 text-white" : "border-slate-700 bg-slate-900 text-slate-300"}`}>{label} · {count}</button>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-bold text-slate-300">{t.found}: {marketTotal}</div>
      </div>

      {callNotice.message ? (
        <div className={`rounded-2xl p-3 text-sm font-black ${
          callNotice.kind === "success"
            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200"
            : callNotice.kind === "warn"
              ? "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-200"
              : "bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-200"
        }`} data-reisor-call-notice="p14">
          {callNotice.message}
        </div>
      ) : null}

      {busy && items.length === 0 ? (
        <div className="flex min-h-[160px] items-center justify-center rounded-2xl border border-slate-800 bg-slate-900/60">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            {t.loading}
          </div>
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-rose-900/60 bg-rose-950/30 p-4 text-sm font-bold text-rose-200">
          {error}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-center text-sm font-bold text-slate-400">
          {t.empty}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const fixedPrice =
              text(item?.price?.mode).toUpperCase() === "FIXED"
                ? formatAmount(
                    item?.price?.amount,
                    item?.price?.currency,
                    lang
                  )
                : "";

            const car = vehicleName(item);
            const nativeReisor = isNativeReisorTransport(item);

            if (nativeReisor) {
              const primary = nativeTransportPrimaryLabel(item, t.vehicle);
              const routeFrom = vehicleDisplayPlace(item?.from);
              const routeTo = vehicleDisplayPlace(item?.to);
              const route = [routeFrom, routeTo].filter(Boolean).join(" → ");
              const countries = Array.isArray(item?.workCountryLabels) && item.workCountryLabels.length
                ? item.workCountryLabels.map((value) => text(value)).filter(Boolean).slice(0, 12)
                : Array.isArray(item?.workCountries)
                  ? item.workCountries.map((value) => text(value).toUpperCase()).filter(Boolean).slice(0, 12)
                  : [];
              const confirmedFree = item?.availabilityConfirmed === true;
              const freshness = formatDate(item?.freshnessAt || item?.updatedAt, lang);
              const contactPhone = text(item?.contact?.phone || item?.contactPhone || item?.phone);

              return (
                <article
                  key={text(item?.id)}
                  className="cursor-pointer overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition active:scale-[0.995] dark:border-slate-700 dark:bg-slate-900"
                  data-reisor-native-transport-card="v2"
                  data-reisor-native-card-clickable="p11"
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedItem(item)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedItem(item);
                    }
                  }}
                >
                  <div className="relative">
                    <ReisorTransportVisual item={item} className="h-[180px] w-full rounded-none" />
                    <div className={`absolute right-3 top-3 rounded-full px-3 py-1.5 text-xs font-black shadow-sm ${confirmedFree ? "bg-emerald-500 text-white" : "bg-amber-100 text-amber-950"}`}>
                      {confirmedFree ? `● ${t.confirmedFree}` : t.unconfirmed}
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-[18px] font-black leading-tight text-slate-950 dark:text-white">{primary}</div>
                        <div className="mt-1 inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-black text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">REISOR ✓</div>
                      </div>
                      <div className="shrink-0 text-right text-[11px] font-bold text-slate-400">{freshness ? `${t.updated}: ${freshness}` : ""}</div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] font-bold text-slate-600 dark:text-slate-300">
                      <span>{vehicleBodyLabel(item, lang)}</span>
                      {positiveMetric(item?.capacityTons, t.tons) ? <><span>·</span><span>{positiveMetric(item?.capacityTons, t.tons)}</span></> : null}
                      {positiveMetric(item?.volumeM3, t.cubic) ? <><span>·</span><span>{positiveMetric(item?.volumeM3, t.cubic)}</span></> : null}
                    </div>

                    {car && car !== primary ? <div className="mt-1 text-[13px] font-semibold text-slate-500 dark:text-slate-400">{car}</div> : null}

                    {route ? (
                      <div className="mt-3 flex items-center gap-2 rounded-2xl bg-emerald-50 px-3 py-2 text-[14px] font-black text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200">
                        <MapPin size={15} className="shrink-0" /><span className="min-w-0 break-words">{route}</span>
                      </div>
                    ) : countries.length ? (
                      <div className="mt-3">
                        <div className="mb-2 text-[12px] font-black uppercase tracking-wide text-slate-400">{t.direction}</div>
                        <div className="flex flex-wrap gap-1.5">{countries.map((country) => <span key={country} className="rounded-full bg-slate-100 px-2.5 py-1 text-[12px] font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">{country}</span>)}</div>
                      </div>
                    ) : null}

                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <button type="button" onClick={(event) => { event.stopPropagation(); void openSendFreight(item); }} disabled={!canSendFreightToVehicle(item) || item?.isOwn === true} className="flex min-h-[50px] items-center justify-center gap-2 rounded-2xl bg-amber-400 px-3 text-[14px] font-black text-slate-950 shadow-sm active:scale-[0.99] disabled:opacity-45"><Send size={18} />{t.sendFreight}</button>
                      {item?.isOwn === true ? (
                        <div className="flex min-h-[50px] items-center justify-center rounded-2xl border border-slate-300 bg-slate-50 px-3 text-center text-[13px] font-black text-slate-600" data-reisor-own-vehicle="p14">{t.ownVehicle || "Это ваша машина."}</div>
                      ) : contactPhone ? (
                        <a
                          href={`tel:${contactPhone.replace(/[^+\d]/g, "")}`}
                          onClick={(event) => event.stopPropagation()}
                          className="flex min-h-[50px] items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-slate-100 px-3 text-[14px] font-black text-slate-950 no-underline active:scale-[0.99]"
                          data-reisor-direct-call="p15"
                        >
                          <Phone size={18} />{t.call || "Позвонить"}
                        </a>
                      ) : (
                        <button type="button" onClick={(event) => { event.stopPropagation(); void requestVehicleCall(item); }} className="flex min-h-[50px] items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-slate-100 px-3 text-[14px] font-black text-slate-950 active:scale-[0.99]" data-reisor-callback-action="p14"><Phone size={18} />{t.callback || "Запросить звонок"}</button>
                      )}
                    </div>
                  </div>
                </article>
              );
            }

            // External/parser cards stay deliberately unchanged.
            return (
              <article
                key={text(item?.id)}
                className="border-b border-slate-800 bg-slate-900/35 px-1 py-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[19px] font-black leading-tight text-white">
                      <MapPin size={17} className="shrink-0 text-blue-400" />
                      <span className="truncate">
                        {vehicleDisplayPlace(item?.from) || "—"}
                      </span>
                      <span className="text-slate-500">→</span>
                      <span className="truncate">
                        {vehicleDisplayPlace(item?.to) || "—"}
                      </span>
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px] font-semibold text-slate-300">
                      <span className="font-black text-blue-300">{vehicleBodyLabel(item, lang)}</span>
                      {positiveMetric(item?.capacityTons, t.tons) ? <><span className="text-slate-600">·</span><span>{positiveMetric(item?.capacityTons, t.tons)}</span></> : null}
                      {positiveMetric(item?.volumeM3, t.cubic) ? <><span className="text-slate-600">·</span><span>{positiveMetric(item?.volumeM3, t.cubic)}</span></> : null}
                      {item?.partialLoad ? <><span className="text-slate-600">·</span><span>{t.partial}</span></> : null}
                    </div>
                  </div>

                  <Truck size={23} className="shrink-0 text-slate-400" />
                </div>

                {car ? (
                  <div className="mt-2 flex items-center gap-2 text-[14px] text-slate-400">
                    <Truck size={15} />
                    <span>{car}</span>
                  </div>
                ) : null}

                {sanitizeExternalExcerpt(item?.rawText || item?.comment) ? (
                  <div className="mt-3 line-clamp-3 whitespace-pre-wrap text-[15px] leading-6 text-slate-300">
                    {sanitizeExternalExcerpt(item?.rawText || item?.comment)}
                  </div>
                ) : null}

                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-slate-500">
                  <span className="flex items-center gap-1.5"><CalendarDays size={14} />{t.available}: {formatDate(item?.availableFrom, lang)}</span>
                  <span>{t.expires}: {formatDate(item?.expiresAt, lang)}</span>
                </div>

                <div className="mt-4 border-t border-slate-800 pt-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-[13px] font-bold text-emerald-300">
                      {fixedPrice || t.negotiable}
                    </div>

                    <button
                      type="button"
                      onClick={() => setSelectedItem(item)}
                      className="flex min-h-[42px] items-center justify-center rounded-xl bg-slate-800 px-4 text-[14px] font-black text-slate-100"
                    >
                      {t.details}
                    </button>
                  </div>

                </div>
              </article>
            );
          })}
        </div>
      )}

      {items.length < marketTotal ? (
        <button type="button" disabled={busy} onClick={() => void load(filters, { append: true, offset: items.length })} className="min-h-[48px] w-full rounded-2xl border border-slate-700 bg-slate-900 text-sm font-black text-slate-200 disabled:opacity-50">
          {busy ? "Загрузка…" : `Показать ещё · ${marketTotal - items.length}`}
        </button>
      ) : null}
      {sendSheet}
    </section>
  );
}
